import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { useTabParam } from "../lib/useTabParam";
import DialPad from "../components/notifications/DialPad";
import ScheduledTextsTab from "../components/notifications/ScheduledTextsTab";

interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  body: string | null;
  deal_id: string | null;
  metadata: Record<string, unknown> | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  // Joined from deals for person filtering
  deal_owner_id?: string | null;
  deal_owner_name?: string | null;
}

type FilterKey = "all" | "unread" | "texts" | "calls" | "coaching" | "deals" | "dispatch" | "system" | "dialpad" | "scheduled";
const FILTER_KEYS: readonly FilterKey[] = ["all", "unread", "texts", "calls", "coaching", "deals", "dispatch", "system", "dialpad", "scheduled"];

const EXEC_ROLES = ["admin", "sales_director", "asm", "roof_director", "director_of_operations", "team_lead"];

const TYPE_ICONS: Record<string, string> = {
  text_received: "\ud83d\udcf1",
  // Homeowner pages -> the team (ho-portal-notify, Oct 21, 2026)
  portal_message: "\ud83d\udcac",
  homeowner_upload: "\ud83d\udcce",
  dep_release_photos: "\ud83d\udcf8",
  color_addendum_signed: "\ud83c\udfa8",
  call_received: "\ud83d\udcde",
  voicemail: "\ud83d\udcde",
  deal_assigned: "\ud83c\udfe0",
  stage_changed: "\u2192",
  approval_needed: "\u2705",
  approval_result: "\ud83d\udccb",
  chad_build_approval: "\ud83d\udd28",
  recanvass_assigned: "\ud83d\udcde",
  recanvass_revived: "\ud83c\udf29\ufe0f",
  recanvass_split_added: "\ud83e\udd1d",
  recanvass_split_replaced: "\u26a0\ufe0f",
  recanvass_owner_replaced: "\ud83d\udd04",
  referral_new: "\ud83e\udd1d",
  referral_earned: "\ud83d\udcb0",
  referral_claim_filed: "\ud83d\udcdd",
  referral_duplicate_claim: "\u26a0\ufe0f",
  referral_approved: "\ud83d\udcb5",
  invoice_sent: "\ud83e\uddfe",
  invoice_drift: "\u26a0\ufe0f",
  financials_pulse: "💹",
  ops_health_alert: "🚨",
  agreement_sent: "\ud83d\udce4",
  adjuster_blast_sent: "\ud83d\udccb",
  chad_agreement_submitted: "\ud83d\udcdd",
  chad_ready_for_dispatch: "\ud83d\ude9a",
  robert_schedule_build: "\ud83d\udee0\ufe0f",
  srs_material_order: "\ud83d\udce6",
  build_rescheduled: "\ud83d\uddd3\ufe0f",
  expected_build_set: "\ud83d\udcc5",
  build_called_off: "\u26d4",
  srs_return_pickup: "\ud83d\ude9a",
  hover_measurements_ready: "\ud83c\udfe0",
  hover_job_failed: "\u26a0\ufe0f",
  coaching_note: "\ud83d\udcac",
  coaching_reply: "\ud83d\udcac",
  coaching_mention: "\ud83c\udff7\ufe0f",
  sla_warning: "\u26a0\ufe0f",
  build_scheduled: "\ud83d\udcc5",
  build_payment_plan: "\ud83d\udcb5",
  payment_received: "\ud83d\udcb5",
  payment_failed: "\u26a0\ufe0f",
  // A bank payment typed in by hand, waiting on the homeowner to confirm
  // Stripe's test deposit (stripe-webhook v19, Oct 20, 2026).
  ach_verify_pending: "\ud83c\udfe6",
  ach_verify_expired: "\u26a0\ufe0f",
  spif_update: "\ud83c\udfc6",
  scoreboard: "\ud83e\udd47",
  system: "\u2699\ufe0f",
  pipeline_alert: "\ud83d\udea8",
  commission: "\ud83d\udcb0",
  commission_validation_blocked: "\ud83d\udea8",
  dispatch_question: "\ud83d\udcac",
  dispatch_question_reply: "\u2705",
  adjuster_meeting_scheduled: "\ud83d\udcc5",
  adjuster_ready: "\ud83d\udccb",
  duplicate_lead: "\u26a0\ufe0f",
  claude_action: "\ud83e\udd20",
  needs_dol_storm_match: "\ud83c\udf29\ufe0f",
  warranty_claim_opened: "\ud83d\udee1\ufe0f",
  warranty_photo_added: "\ud83d\udcf8",
  warranty_status_changed: "\u2705",
  warranty_overdue: "\u23f1",
  canvas_appt_approved: "\u2705",
  canvas_appt_rejected: "\u274c",
  canvas_pin_built: "\ud83c\udfe0",
  canvas_pin_drone_rejected: "\ud83d\udee9",
  canvas_pin_homeowner_declined: "\ud83d\udc4b",
  canvas_shift_auto_ended: "\ud83c\udf19",
  canvas_session_auto_closed: "\u23f1\ufe0f",
  canvasser_below_minimum: "\ud83d\udcc9",
  commitment_change_request: "\ud83d\udcc5",
  commitment_change_resolved: "\u2705",
  commission_tier_promoted: "\ud83c\udf89",
  commission_tier_nudge: "\ud83d\udd25",
  play_crew_added: "\ud83e\udd1d",
  // Ops Requests (Sep 25, 2026)
  ops_request_new: "\ud83d\udee0",
  team_knock: "\ud83d\udeaa",
  daily_commit_reminder: "\u23f0",
  daily_commit_late: "\u26a0\ufe0f",
  daily_plan_change: "\ud83d\udcc5",
  daily_plans_summary: "\ud83d\udccb",
  ops_request_update: "\ud83d\udee0",
  ops_request_done: "\u2705",
  ops_request_withdrawn: "\u21a9\ufe0f",
  ops_request_comment: "\ud83d\udcac",
  ops_request_reassigned: "\ud83d\udd01",
  // Homeowner texts + emails engine (Oct 20, 2026)
  feedback_low: "\u2b50",
  hc_burst_held: "\u23f8\ufe0f",
  hc_reminders_done: "\ud83d\udd14",
  // JAC's invoice differed from the sales order and Shannon adjusted it in Books (Oct 29, 2026; to Chad)
  trade_invoice_discrepancy: "\ud83e\uddfe",
};

const TYPE_COLORS: Record<string, string> = {
  text_received: "bg-blue-50 text-blue-600",
  portal_message: "bg-blue-50 text-blue-600",
  homeowner_upload: "bg-sky-50 text-sky-600",
  dep_release_photos: "bg-amber-50 text-amber-700",
  color_addendum_signed: "bg-emerald-50 text-emerald-600",
  call_received: "bg-purple-50 text-purple-600",
  voicemail: "bg-purple-50 text-purple-600",
  deal_assigned: "bg-emerald-50 text-emerald-600",
  stage_changed: "bg-slate-100 text-slate-600",
  approval_needed: "bg-amber-50 text-amber-600",
  approval_result: "bg-emerald-50 text-emerald-600",
  chad_build_approval: "bg-amber-50 text-amber-600",
  recanvass_assigned: "bg-amber-50 text-amber-700",
  recanvass_revived: "bg-sky-50 text-sky-700",
  recanvass_split_added: "bg-violet-50 text-violet-700",
  recanvass_split_replaced: "bg-rose-50 text-rose-700",
  recanvass_owner_replaced: "bg-rose-50 text-rose-700",
  referral_new: "bg-orange-50 text-orange-600",
  referral_earned: "bg-emerald-50 text-emerald-600",
  referral_claim_filed: "bg-sky-50 text-sky-600",
  referral_duplicate_claim: "bg-amber-50 text-amber-700",
  referral_approved: "bg-emerald-50 text-emerald-600",
  invoice_sent: "bg-emerald-50 text-emerald-600",
  invoice_drift: "bg-amber-50 text-amber-600",
  financials_pulse: "bg-emerald-50 text-emerald-600",
  ops_health_alert: "bg-rose-50 text-rose-600",
  agreement_sent: "bg-orange-50 text-orange-700",
  adjuster_blast_sent: "bg-indigo-50 text-indigo-600",
  chad_agreement_submitted: "bg-amber-50 text-amber-600",
  chad_ready_for_dispatch: "bg-amber-50 text-amber-600",
  robert_schedule_build: "bg-indigo-50 text-indigo-600",
  srs_material_order: "bg-emerald-50 text-emerald-600",
  build_rescheduled: "bg-amber-50 text-amber-600",
  expected_build_set: "bg-amber-50 text-amber-600",
  build_called_off: "bg-rose-50 text-rose-600",
  srs_return_pickup: "bg-emerald-50 text-emerald-600",
  hover_measurements_ready: "bg-emerald-50 text-emerald-600",
  hover_job_failed: "bg-rose-50 text-rose-600",
  coaching_note: "bg-amber-50 text-amber-600",
  coaching_reply: "bg-amber-50 text-amber-600",
  coaching_mention: "bg-teal-50 text-bardin-teal",
  sla_warning: "bg-red-50 text-red-600",
  build_scheduled: "bg-indigo-50 text-indigo-600",
  build_payment_plan: "bg-amber-50 text-amber-700",
  payment_received: "bg-green-50 text-green-600",
  payment_failed: "bg-red-50 text-red-700",
  ach_verify_pending: "bg-amber-50 text-amber-700",
  ach_verify_expired: "bg-red-50 text-red-700",
  spif_update: "bg-amber-50 text-amber-600",
  scoreboard: "bg-amber-50 text-amber-600",
  system: "bg-slate-100 text-slate-500",
  pipeline_alert: "bg-red-50 text-red-600",
  commission: "bg-green-50 text-green-600",
  commission_validation_blocked: "bg-red-50 text-red-700",
  dispatch_question: "bg-amber-50 text-amber-600",
  dispatch_question_reply: "bg-emerald-50 text-emerald-600",
  adjuster_meeting_scheduled: "bg-indigo-50 text-indigo-600",
  adjuster_ready: "bg-orange-50 text-orange-700",
  duplicate_lead: "bg-amber-50 text-amber-700",
  claude_action: "bg-orange-50 text-orange-700",
  needs_dol_storm_match: "bg-amber-50 text-amber-700",
  warranty_claim_opened: "bg-amber-50 text-amber-700",
  warranty_photo_added: "bg-amber-50 text-amber-600",
  warranty_status_changed: "bg-slate-100 text-slate-600",
  warranty_overdue: "bg-red-50 text-red-700",
  canvas_appt_approved: "bg-emerald-50 text-emerald-600",
  canvas_appt_rejected: "bg-red-50 text-red-600",
  canvas_pin_built: "bg-emerald-50 text-emerald-600",
  canvas_pin_drone_rejected: "bg-amber-50 text-amber-700",
  canvas_pin_homeowner_declined: "bg-slate-100 text-slate-600",
  canvas_shift_auto_ended: "bg-slate-100 text-slate-600",
  canvas_session_auto_closed: "bg-slate-100 text-slate-600",
  canvasser_below_minimum: "bg-amber-50 text-amber-600",
  commitment_change_request: "bg-amber-50 text-amber-700",
  commitment_change_resolved: "bg-emerald-50 text-emerald-700",
  commission_tier_promoted: "bg-green-50 text-green-600",
  commission_tier_nudge: "bg-orange-50 text-orange-700",
  play_crew_added: "bg-teal-50 text-bardin-teal",
  ops_request_new: "bg-amber-50 text-amber-600",
  team_knock: "bg-orange-50 text-orange-600",
  daily_commit_reminder: "bg-amber-50 text-amber-600",
  daily_commit_late: "bg-rose-50 text-rose-600",
  daily_plan_change: "bg-amber-50 text-amber-600",
  daily_plans_summary: "bg-emerald-50 text-emerald-600",
  ops_request_update: "bg-slate-100 text-slate-600",
  ops_request_done: "bg-emerald-50 text-emerald-600",
  ops_request_withdrawn: "bg-slate-100 text-slate-500",
  ops_request_comment: "bg-sky-50 text-sky-600",
  ops_request_reassigned: "bg-violet-50 text-violet-600",
  feedback_low: "bg-rose-50 text-rose-600",
  hc_burst_held: "bg-amber-50 text-amber-700",
  hc_reminders_done: "bg-orange-50 text-orange-700",
  trade_invoice_discrepancy: "bg-amber-50 text-amber-700",
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const hrs = Math.floor(diffMins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

const FILTER_TO_TYPES: Record<FilterKey, string[] | null> = {
  all: null,
  unread: null,
  texts: ["text_received", "portal_message"],
  calls: ["call_received", "voicemail"],
  coaching: ["coaching_note", "coaching_reply", "coaching_mention", "commitment_change_request", "commitment_change_resolved", "team_knock", "daily_commit_reminder", "daily_commit_late", "daily_plan_change", "daily_plans_summary", "canvasser_below_minimum"],
  deals: ["deal_assigned", "stage_changed", "approval_needed", "approval_result", "chad_build_approval", "invoice_sent", "agreement_sent", "chad_agreement_submitted", "chad_ready_for_dispatch", "robert_schedule_build", "build_scheduled", "build_payment_plan", "adjuster_ready", "payment_received", "payment_failed", "ach_verify_pending", "ach_verify_expired", "duplicate_lead", "claude_action", "needs_dol_storm_match", "warranty_claim_opened", "warranty_photo_added", "warranty_status_changed", "warranty_overdue", "canvas_appt_approved", "canvas_appt_rejected", "canvas_pin_built", "canvas_pin_drone_rejected", "canvas_pin_homeowner_declined", "referral_new", "referral_earned", "referral_claim_filed", "referral_duplicate_claim", "referral_approved", "recanvass_assigned", "recanvass_revived", "recanvass_split_added", "recanvass_split_replaced", "recanvass_owner_replaced", "feedback_low", "hc_reminders_done", "homeowner_upload", "dep_release_photos", "color_addendum_signed", "trade_invoice_discrepancy"],
  // Dispatch Q&A — internal team Q&A about deals. Hidden from the default All
  // view via the exclusion list below to keep the main feed uncluttered;
  // surfaces in this filter or when it's >4hrs unanswered (bubbled into All).
  dispatch: ["dispatch_question", "dispatch_question_reply", "adjuster_meeting_scheduled", "srs_material_order", "srs_return_pickup", "build_rescheduled", "build_called_off", "expected_build_set", "hover_measurements_ready", "hover_job_failed", "ops_request_new", "ops_request_update", "ops_request_done", "ops_request_withdrawn", "ops_request_comment", "ops_request_reassigned"],
  system: ["sla_warning", "spif_update", "scoreboard", "system", "pipeline_alert", "commission", "commission_tier_promoted", "commission_tier_nudge", "commission_validation_blocked", "adjuster_blast_sent", "canvas_shift_auto_ended", "canvas_session_auto_closed", "play_crew_added", "invoice_drift", "financials_pulse", "ops_health_alert", "hc_burst_held"],
  dialpad: null, // dial pad replaces the list view; type filter is unused
  scheduled: null, // scheduled-texts component replaces the list view; type filter unused
};

// Types kept out of the "All" feed (still appear under their own filter pill).
// Dispatch Q&A is high-volume internal chatter — main feed shouldn't be clogged.
const ALL_VIEW_EXCLUDED_TYPES = new Set(["dispatch_question", "dispatch_question_reply"]);

interface TeamMemberOption {
  id: string;
  full_name: string;
  role: string;
}

export default function Notifications() {
  const { member, role } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useTabParam<FilterKey>("tab", "all", FILTER_KEYS);
  const [showCount, setShowCount] = useState(50);
  const [teamMembers, setTeamMembers] = useState<TeamMemberOption[]>([]);
  const [personFilter, setPersonFilter] = useState<string>("mine");

  const isExec = !!role && EXEC_ROLES.includes(role);

  // Load team members for the person filter (exec only)
  useEffect(() => {
    if (!isExec) return;
    supabase
      .from("team_roster")
      .select("id,full_name,role")
      .eq("is_active", true)
      .order("full_name")
      .then(({ data }) => {
        if (data) setTeamMembers(data as TeamMemberOption[]);
      });
  }, [isExec]);

  const load = useCallback(async () => {
    if (!member) return;
    setLoading(true);

    if (isExec && personFilter !== "mine") {
      // Exec view: load notifications then enrich with deal owner.
      //
      // PostgREST hard-caps at 1000 rows per query, and the old .limit(500)
      // was already silently clipping older items as notifications grew (753
      // rows on May 18, 2026, growing every Twilio mirror + deal event).
      // Person filter is applied client-side AFTER load, so clipping breaks
      // exec drill-downs to specific people whose recent activity falls
      // outside the cap. Paginate via .range() to 5K — safe upper bound for
      // the in-memory list view without UI slowdown. Add a date-window
      // filter here if/when the table outgrows 5K and exec only cares about
      // the last N days.
      const NOTIF_CHUNK = 1000;
      const NOTIF_SAFETY_CAP = 5000;
      const notifAll: any[] = [];
      for (let from = 0; from < NOTIF_SAFETY_CAP; from += NOTIF_CHUNK) {
        const { data: chunkData, error: chunkErr } = await supabase
          .from("notifications")
          .select("*")
          .order("created_at", { ascending: false })
          .range(from, from + NOTIF_CHUNK - 1);
        if (chunkErr) break;
        const chunk = (chunkData ?? []) as any[];
        notifAll.push(...chunk);
        if (chunk.length < NOTIF_CHUNK) break;
      }
      const notifData = notifAll;

      if (notifData) {
        // Get deal owners for all deal_ids in one query
        const dealIds = [...new Set((notifData as any[]).map((n) => n.deal_id).filter(Boolean))];
        let ownerMap: Record<string, string> = {};
        if (dealIds.length > 0) {
          const { data: deals } = await supabase
            .from("deals")
            .select("id,owner_id")
            .in("id", dealIds);
          if (deals) {
            deals.forEach((d: any) => { if (d.owner_id) ownerMap[d.id] = d.owner_id; });
          }
        }

        const enriched = (notifData as any[]).map((n) => ({
          ...n,
          deal_owner_id: n.deal_id ? ownerMap[n.deal_id] ?? null : null,
        }));

        // Deduplicate by id
        const seen = new Set<string>();
        const deduped = enriched.filter((n) => {
          if (seen.has(n.id)) return false;
          seen.add(n.id);
          return true;
        });
        setNotifications(deduped as Notification[]);
      }
    } else {
      // RM view or exec "My notifications": only their own
      const { data } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", member.id)
        .order("created_at", { ascending: false })
        .limit(200);
      if (data) setNotifications(data as Notification[]);
    }
    setLoading(false);
  }, [member, isExec, personFilter]);

  useEffect(() => { load(); }, [load]);

  // Real-time
  useEffect(() => {
    if (!member) return;
    const channel = supabase
      .channel("notif-page-realtime")
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "notifications",
        filter: `user_id=eq.${member.id}`,
      }, (payload) => {
        setNotifications((prev) => [payload.new as Notification, ...prev]);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [member]);

  // Filter by type. Default "all" view excludes high-volume internal types
  // (dispatch Q&A) unless they're stale — those bubble into All only after 4hrs
  // unanswered/unread, so nothing critical gets lost behind a filter pill.
  const typeFiltered = notifications.filter((n) => {
    if (filter === "unread") return !n.is_read;
    const types = FILTER_TO_TYPES[filter];
    if (types) return types.includes(n.type);
    // filter === "all"
    if (ALL_VIEW_EXCLUDED_TYPES.has(n.type)) {
      const ageHrs = (Date.now() - new Date(n.created_at).getTime()) / 3_600_000;
      return !n.is_read && ageHrs >= 4; // surface only if stale + unread
    }
    return true;
  });

  // For exec person filter, further filter by deal owner if a specific person is selected
  const filtered = useMemo(() => {
    if (!isExec || personFilter === "mine" || personFilter === "all") return typeFiltered;
    return typeFiltered.filter((n) =>
      n.user_id === personFilter || n.deal_owner_id === personFilter
    );
  }, [typeFiltered, isExec, personFilter]);

  const visible = filtered.slice(0, showCount);
  const hasMore = filtered.length > showCount;
  const unreadTotal = notifications.filter((n) => !n.is_read).length;

  // Group team members for the person filter dropdown
  const rmOptions = useMemo(() => teamMembers.filter((m) => m.role === "restore_manager"), [teamMembers]);
  const tlOptions = useMemo(() => teamMembers.filter((m) => m.role === "team_lead"), [teamMembers]);
  const asmOptions = useMemo(() => teamMembers.filter((m) => m.role === "asm"), [teamMembers]);

  async function markAllRead() {
    if (!member) return;
    await supabase
      .from("notifications")
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq("user_id", member.id)
      .eq("is_read", false);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  }

  async function handleClick(n: Notification) {
    if (!n.is_read) {
      await supabase
        .from("notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("id", n.id);
      setNotifications((prev) =>
        prev.map((p) => (p.id === n.id ? { ...p, is_read: true } : p))
      );
    }
    // Ops Requests link to the request itself (metadata.url = /requests?focus=…),
    // not the deal's Comms tab — the deal_id is there for the person filter.
    // Referral notifications carry a full app URL (the same link their texts
    // use) and open the Referrals board.
    const metaUrl = n.metadata && typeof n.metadata.url === "string" ? n.metadata.url : null;
    const metaPath = metaUrl ? metaUrl.replace(/^https:\/\/app\.bardinroofing\.com(?=\/)/, "") : null;
    if ((n.type.startsWith("ops_request_") || n.type.startsWith("daily_") || n.type === "team_knock"
         || n.type.startsWith("referral_") || n.type === "canvasser_below_minimum"
         || n.type === "canvas_session_auto_closed" || n.type === "expected_build_set"
         // Oct 29, 2026: a signed trade add-on opens the deal on that trade (?trade=), where Schedule is
         || n.type === "trade_addendum_signed") && metaPath && metaPath.startsWith("/")) {
      navigate(metaPath);
      return;
    }
    if (n.deal_id) {
      const tabMap: Record<string, string> = {
        text_received: "comms",
        call_received: "comms",
        voicemail: "comms",
        coaching_note: "coaching",
        coaching_reply: "coaching",
        coaching_mention: "coaching",
        approval_needed: "agreement",
        approval_result: "agreement",
        payment_received: "stage",
        stage_changed: "stage",
        ach_verify_pending: "payments",
        ach_verify_expired: "payments",
        homeowner_upload: "documents",
        dep_release_photos: "documents",
        // the deal's Trades card (Overview), where the order's chip and Chad's price review sit
        trade_invoice_discrepancy: "overview",
      };
      const tab = tabMap[n.type] || "comms";
      navigate(`/deal/${n.deal_id}?tab=${tab}`);
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-bardin-ink">Notifications</h1>
          {unreadTotal > 0 && (
            <p className="text-sm text-slate-500 mt-0.5">{unreadTotal} unread</p>
          )}
        </div>
        {unreadTotal > 0 && (
          <button
            onClick={markAllRead}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-bardin-teal text-white hover:bg-bardin-teal/90 transition"
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* Person filter — exec roles only */}
      {isExec && (
        <div className="bg-white border border-slate-200 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">View:</span>
            <button
              onClick={() => setPersonFilter("mine")}
              className={
                "px-3 py-1.5 rounded-full text-[12px] font-medium border transition " +
                (personFilter === "mine"
                  ? "bg-teal-500 text-white border-teal-500"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")
              }
            >
              My notifications
            </button>
            <button
              onClick={() => setPersonFilter("all")}
              className={
                "px-3 py-1.5 rounded-full text-[12px] font-medium border transition " +
                (personFilter === "all"
                  ? "bg-teal-500 text-white border-teal-500"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")
              }
            >
              All team
            </button>
            <select
              value={personFilter === "mine" || personFilter === "all" ? "" : personFilter}
              onChange={(e) => { if (e.target.value) setPersonFilter(e.target.value); }}
              className="text-[12px] border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-700"
            >
              <option value="">Filter by person…</option>
              {asmOptions.length > 0 && (
                <optgroup label="ASM">
                  {asmOptions.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
                </optgroup>
              )}
              {tlOptions.length > 0 && (
                <optgroup label="Team Leads">
                  {tlOptions.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
                </optgroup>
              )}
              {rmOptions.length > 0 && (
                <optgroup label="Restore Managers">
                  {rmOptions.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
                </optgroup>
              )}
            </select>
            {personFilter !== "mine" && personFilter !== "all" && (
              <span className="text-[11px] text-teal-600 font-semibold">
                Showing: {teamMembers.find((m) => m.id === personFilter)?.full_name ?? "—"}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Type filter bar */}
      <div className="flex gap-2 flex-wrap mb-4">
        {([
          ["all", "All"],
          ["unread", "Unread"],
          ["texts", "Texts"],
          ["calls", "Calls"],
          ["coaching", "Coaching"],
          ["dispatch", "Dispatch Q&A"],
          ["deals", "Deals"],
          ["system", "System"],
          ["dialpad", "📟 Dial Pad"],
          ["scheduled", "📅 Scheduled"],
        ] as [FilterKey, string][]).map(([key, label]) => (
          <button
            key={key}
            onClick={() => { setFilter(key); setShowCount(50); }}
            className={`px-3 py-1.5 text-sm rounded-lg font-medium transition ${
              filter === key
                ? "bg-bardin-teal text-white"
                : "bg-white text-slate-600 border border-slate-200 hover:border-bardin-teal/30"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Dial Pad mode — renders in place of the list */}
      {filter === "dialpad" ? (
        <DialPad />
      ) : filter === "scheduled" ? (
        <ScheduledTextsTab member={member} />
      ) : loading ? (
        <div className="text-center py-12 text-slate-400 animate-pulse">Loading notifications...</div>
      ) : visible.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow">
          <p className="text-slate-400 text-sm">
            {filter === "unread" ? "No unread notifications" : "No notifications yet"}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow divide-y divide-slate-100">
          {visible.map((n) => (
            <button
              key={n.id}
              onClick={() => handleClick(n)}
              className={`w-full text-left px-4 py-4 flex gap-3 hover:bg-slate-50 transition ${
                !n.is_read ? "bg-bardin-sky/15" : ""
              }`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-base ${
                TYPE_COLORS[n.type] ?? "bg-slate-100 text-slate-500"
              }`}>
                {TYPE_ICONS[n.type] ?? "\ud83d\udd14"}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <span className={`text-sm leading-snug ${!n.is_read ? "font-semibold text-bardin-ink" : "text-slate-700"}`}>
                    {n.title}
                  </span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-slate-400 tabular-nums">{formatTime(n.created_at)}</span>
                    {!n.is_read && (
                      <span className="w-2.5 h-2.5 rounded-full bg-bardin-teal flex-shrink-0" />
                    )}
                  </div>
                </div>
                {n.body && (
                  <p className="text-sm text-slate-500 mt-1 line-clamp-2">{n.body}</p>
                )}
                {n.type === "voicemail" && n.metadata && typeof n.metadata.recording_url === "string" && (
                  <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                    <audio controls preload="none" className="h-8 w-full max-w-xs">
                      <source src={n.metadata.recording_url} type="audio/mpeg" />
                    </audio>
                  </div>
                )}
                {n.deal_id ? (
                  <span className="text-xs text-bardin-teal mt-1 inline-block">Open deal → Comms</span>
                ) : (
                  <span className="text-xs text-slate-400 mt-1 inline-block">No deal linked — unknown number</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {filter !== "dialpad" && hasMore && (
        <div className="text-center mt-4">
          <button
            onClick={() => setShowCount((c) => c + 50)}
            className="px-4 py-2 text-sm text-bardin-teal hover:bg-bardin-teal/5 rounded-lg transition"
          >
            Load more ({filtered.length - showCount} remaining)
          </button>
        </div>
      )}
    </div>
  );
}
